import { DeckClown, DeckDrink, type PublicCard } from '@/api/generated/game';

import { deckLook } from './decks';

const REQUIREMENT_LABEL: Record<string, string> = {
  timer: 'нужен таймер',
  coin: 'нужна монета',
  dice: 'нужен кубик',
  camera: 'нужна камера',
  randomizer: 'случайное имя',
  minigame: 'мини-игра',
};

export interface CardFaceProps {
  card: PublicCard;
  /** Кому адресовано — строкой, уже собранной вызывающим. */
  addressee?: string;
}

/**
 * Лицо карточки по анатомии из design-spec.md: поле цвета колоды, огромное
 * эмодзи в фактуре, шапка с названием, задание, подвал через правило 3px.
 *
 * Колода Клоуна ломает систему намеренно: дисплейный шрифт в теле задания,
 * штриховка и чёрная рамка внутрь. Это не украшение — по ней стол понимает,
 * что происходит нечто, ещё не читая текст.
 *
 * `{target}` в тексте не прячется: в боевом контенте его нет, и доехавший до
 * экрана литерал означает баг контента, который должно быть видно
 * (`../clown-back/api/ws-protocol.md`).
 */
export function CardFace({ card, addressee }: CardFaceProps) {
  const look = deckLook(card.deck);
  const isClown = card.deck === DeckClown;
  // Карты алкоголя короткие — «выпей три глотка» и всё, — поэтому набраны
  // крупнее и стоят просторнее: иначе половина поля пустует зря.
  const isDrink = card.deck === DeckDrink;

  return (
    <div
      className={`relative flex min-h-draw flex-col overflow-hidden ${look.face} text-ground ${
        isDrink ? 'p-8' : 'p-6'
      } ${isClown ? 'bg-hatch' : ''}`}
      style={isClown ? { boxShadow: 'inset 0 0 0 7px #0C0C0C' } : undefined}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-8 -right-8 select-none text-mark opacity-25"
      >
        {look.emoji}
      </span>

      <div className="relative flex items-center gap-3">
        <span className="text-meta uppercase tracking-widest">{look.title}</span>
        <span aria-hidden className="text-row">
          {look.emoji}
        </span>
      </div>

      <p
        className={`relative mt-6 flex-1 ${
          isClown
            ? 'font-display text-shout text-twin'
            : isDrink
              ? 'font-body text-shout font-semibold'
              : 'font-body text-task font-semibold'
        }`}
      >
        {card.text}
      </p>

      <div className="relative mt-6 flex flex-wrap items-center gap-3 border-t-3 border-ground pt-3 text-meta uppercase tracking-widest">
        <span>острота {card.intensity}</span>
        {addressee && <span>кому: {addressee}</span>}
        {card.requires?.map((requirement) => (
          <span key={requirement}>{REQUIREMENT_LABEL[requirement] ?? requirement}</span>
        ))}
        {card.duration != null && <span>{card.duration} с</span>}
        {card.minigame && <span>{card.minigame}</span>}
      </div>
    </div>
  );
}
